import React from 'react';
import { Card } from '@/components/ui/card';
import { Loader2, Sparkles } from 'lucide-react';
import { CoverPreviewOverlay } from './CoverPreviewOverlay';
import { WIZARD_COVER_PREVIEW_TIME, STORY_TEXT_TIME } from '@/lib/seo/site-facts';

interface LoadingOverlayProps {
  operation: 'story' | 'preview' | 'cover' | null;
  childName?: string;
  characterCount?: number;
  characterNames?: string[];
  mainCharacterName?: string;
  isMultiCharacter?: boolean;
  coverData?: any; // NEW: Cover data for cover-first mode
}

export function LoadingOverlay({ 
  operation, 
  childName = 'your child',
  characterCount = 1,
  characterNames = [],
  mainCharacterName,
  isMultiCharacter = false,
  coverData // NEW
}: LoadingOverlayProps) {
  if (!operation) return null;
  
  // If we have cover data and operation is 'cover', show the cover preview
  if (operation === 'cover' && coverData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="max-w-lg mx-4">
          <CoverPreviewOverlay 
            coverData={coverData}
            isGeneratingStory={true}
            estimatedTimeRemaining={isMultiCharacter ? 60 : 30}
          />
        </div>
      </div>
    );
  }
  
  const getOperationContent = () => {
    switch (operation) {
      case 'story':
        if (isMultiCharacter) {
          return {
            title: "Creating Your Story",
            message: `We're crafting a personalized adventure featuring ${mainCharacterName || characterNames[0]} and ${characterCount - 1} other${characterCount > 2 ? 's' : ''}...`,
            timeEstimate: `This usually takes ${STORY_TEXT_TIME}`,
            steps: [
              `✨ Analyzing ${characterCount} characters...`,
              '🎭 Coordinating character interactions...',
              '📖 Creating multi-character story plot...',
              '🎨 Preparing character-specific illustrations...'
            ]
          };
        }
        return {
          title: "Creating Your Magical Story",
          message: `We're crafting a personalized adventure for ${childName}...`,
          timeEstimate: `This usually takes ${STORY_TEXT_TIME}`,
          steps: [
            '✨ Analyzing character details...',
            '📖 Creating unique story plot...',
            '🎨 Preparing illustration descriptions...'
          ]
        };
        
      case 'preview':
        if (isMultiCharacter) {
          return {
            title: "Creating Your Book",
            message: `We're generating personalized illustrations for all ${characterCount} characters...`,
            timeEstimate: `This usually takes ${WIZARD_COVER_PREVIEW_TIME}`,
            steps: [
              '🎨 Generating cover with all characters...',
              '👥 Ensuring character consistency...',
              '✨ Creating personalized artwork...',
              '📖 Preparing your book...'
            ]
          };
        }
        return {
          title: "Creating Your Preview",
          message: `We're generating personalized illustrations for ${childName}...`,
          timeEstimate: `This usually takes ${WIZARD_COVER_PREVIEW_TIME}`,
          steps: [
            '🎨 Generating cover illustration...',
            '✨ Creating personalized artwork...',
            '📖 Preparing book preview...'
          ]
        };
        
      default:
        return null;
    }
  };
  
  const content = getOperationContent();
  if (!content) return null;
  
  // Calculate progress width based on operation
  const progressWidth = operation === 'story' ? '60%' : '80%';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="max-w-md mx-4 p-8">
        <div className="text-center space-y-4">
          <div className="relative w-20 h-20 mx-auto">
            <Loader2 className="w-20 h-20 text-purple-600 animate-spin" />
            <Sparkles className="w-10 h-10 text-pink-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          
          <h3 className="text-xl font-semibold">{content.title}</h3>
          <p className="text-gray-600">{content.message}</p>
          
          <div className="space-y-2">
            <p className="text-sm text-gray-500">{content.timeEstimate}</p>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full animate-pulse transition-all duration-1000" 
                style={{ width: progressWidth }}
              />
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              {content.steps.map((step, index) => (
                <p key={index}>{step}</p>
              ))}
            </div>
          </div>
          
          <p className="text-xs text-gray-400 mt-4">
            Your {isMultiCharacter ? 'multi-character ' : ''}{operation === 'story' ? 'story' : 'book'} is being generated in the background
          </p>
        </div>
      </Card>
    </div>
  );
}
